import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingCart, Plus, Check, ArrowRight, Info, Home, ExternalLink, Loader2, ChevronDown, ChevronUp, Sparkles, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getShopifyProduct, getShopifyProducts, createShopifyCart, getShopifyConfig, trackAnalyticsEvent } from '@/lib/api';

const ResultsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  
  const [result, setResult] = useState(null);
  const [shopifyProducts, setShopifyProducts] = useState({});
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingShopify, setLoadingShopify] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [shopifyConfigured, setShopifyConfigured] = useState(false);
  const [expandedProtocols, setExpandedProtocols] = useState({});

  useEffect(() => {
    loadResults();
    checkShopifyConfig();
  }, []);

  const checkShopifyConfig = async () => {
    try {
      const config = await getShopifyConfig();
      setShopifyConfigured(config.configured);
    } catch (error) {
      console.error('Failed to check Shopify config:', error);
    }
  };

  const loadResults = () => {
    const stored = sessionStorage.getItem('quizResult');
    if (stored) {
      const data = JSON.parse(stored);
      setResult(data);
      
      // Expand the first protocol by default
      if (data.recommended_protocols?.length > 0) {
        setExpandedProtocols({ [data.recommended_protocols[0].id]: true });
      }
      
      // Collect all unique products across all protocols + cross-sell
      const allProducts = [];
      const seenIds = new Set();
      (data.recommended_protocols || []).forEach(proto => {
        (proto.products || []).forEach(p => {
          if (!seenIds.has(p.id)) {
            seenIds.add(p.id);
            allProducts.push(p);
          }
        });
      });
      (data.cross_sell_products || []).forEach(p => {
        if (!seenIds.has(p.id)) {
          seenIds.add(p.id);
          allProducts.push(p);
        }
      });
      
      if (allProducts.length > 0) {
        loadShopifyProducts(allProducts);
      }
    }
    setLoading(false);
  };

  const loadShopifyProducts = async (products) => {
    setLoadingShopify(true);
    const shopifyData = {};
    
    // Only fetch products that have a shopify_handle and need live data
    const productsToFetch = products.filter(p => p.shopify_handle);
    
    // Batch fetch using the batch endpoint instead of individual calls
    if (productsToFetch.length > 0) {
      try {
        const handles = productsToFetch.map(p => p.shopify_handle);
        const batchResult = await getShopifyProducts(handles);
        // Map batch results back to product IDs
        const handleMap = {};
        productsToFetch.forEach(p => { handleMap[p.shopify_handle] = p.id; });
        (batchResult || []).forEach(sp => {
          const productId = handleMap[sp.handle];
          if (productId) shopifyData[productId] = sp;
        });
      } catch (error) {
        console.warn('Batch Shopify fetch failed, falling back to local data');
      }
    }
    
    setShopifyProducts(shopifyData);
    setLoadingShopify(false);
  };

  const getProductData = (product) => {
    const shopifyData = shopifyProducts[product.id];
    if (shopifyData && !shopifyData.error) {
      return {
        ...product,
        title: shopifyData.title || product.product_name,
        price: shopifyData.price || product.price,
        image_url: shopifyData.image_url || product.image_url,
        variant_id: shopifyData.variant_id,
        available: shopifyData.available !== false
      };
    }
    return product;
  };

  const addToCart = (product) => {
    const productData = getProductData(product);
    if (!cart.find(p => p.originalId === product.id)) {
      setCart([...cart, { ...productData, originalId: product.id }]);
      toast.success(`${productData.title || productData.product_name} added`);
      trackAnalyticsEvent('add_to_cart', {
        product_id: product.id,
        product_name: productData.title || productData.product_name || product.product_name,
      });
    }
  };

  const addProtocolToCart = (protocol) => {
    const newCart = [...cart];
    let added = 0;
    (protocol.products || []).forEach(product => {
      if (!newCart.find(p => p.originalId === product.id)) {
        const productData = getProductData(product);
        newCart.push({ ...productData, originalId: product.id });
        added++;
      }
    });
    setCart(newCart);
    if (added > 0) toast.success(`${added} product${added > 1 ? 's' : ''} added to cart`);
  };

  const proceedToCheckout = async () => {
    if (cart.length === 0) return toast.error('Your cart is empty');
    const variantIds = cart.filter(i => i.variant_id).map(i => i.variant_id);
    if (variantIds.length === 0) return toast.error('No Shopify products in cart');

    setCheckingOut(true);
    try {
      const cartData = await createShopifyCart(variantIds);
      if (cartData.checkout_url) {
        window.open(cartData.checkout_url, '_blank');
        toast.success('Redirecting to checkout...');
      } else {
        toast.error('Failed to create checkout');
      }
    } catch {
      toast.error('Failed to create checkout. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  const isInCart = (productId) => cart.some(p => p.originalId === productId);

  const toggleProtocol = (protocolId) => {
    setExpandedProtocols(prev => ({ ...prev, [protocolId]: !prev[protocolId] }));
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'primary':
        return <Badge className="badge-primary" data-testid={`badge-${role}`}>Core</Badge>;
      case 'secondary':
        return <Badge className="badge-secondary" data-testid={`badge-${role}`}>Targeted</Badge>;
      default:
        return <Badge className="badge-optional" data-testid={`badge-${role}`}>Optional</Badge>;
    }
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return null;
    return `$${parseFloat(price).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center" data-testid="results-loading">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-[#d4e4bc] mx-auto animate-pulse" />
          <p className="mt-4 text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center" data-testid="results-empty">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-[#d4e4bc] flex items-center justify-center mx-auto mb-6">
            <Info className="w-8 h-8 text-[#1a1a1a]" strokeWidth={1.5} />
          </div>
          <h2 className="font-serif text-2xl text-[#1a1a1a] mb-4">No Results Found</h2>
          <p className="text-gray-600 mb-8 text-sm">
            It looks like you haven't completed the quiz yet. 
            Take our quick assessment to get your personalized supplement recommendations.
          </p>
          <Button onClick={() => navigate(isEmbed ? '/embed' : '/quiz')} className="btn-primary" data-testid="take-quiz-btn">
            Take the Quiz <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    );
  }

  const protocols = result.recommended_protocols || (result.recommended_protocol ? [result.recommended_protocol] : []);

  return (
    <div className={`min-h-screen bg-[#F7F5F0] ${isEmbed ? 'embed-mode' : ''}`} data-testid="results-page">
      {/* Header */}
      {!isEmbed && (
        <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <span className="font-serif text-xl text-[#1a1a1a]">Fast Track To Health</span>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-600" data-testid="home-btn">
                <Home className="w-5 h-5 mr-2" strokeWidth={1.5} /> Home
              </Button>
              {cart.length > 0 && (
                <Button onClick={proceedToCheckout} disabled={checkingOut} className="btn-primary" data-testid="checkout-btn">
                  {checkingOut ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ShoppingCart className="w-5 h-5 mr-2" strokeWidth={1.5} />}
                  Checkout ({cart.length})
                </Button>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Hero */}
      <section className="hero-section py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-[#d4e4bc] flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-[#1a1a1a]" strokeWidth={1.5} />
          </div>
          <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4">Your Results</p>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#1a1a1a] mb-6">
            Your Personalized Nutritional Support Plan
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">{result.explanation}</p>
        </div>
      </section>

      {/* Support Areas */}
      {result.top_conditions?.length > 0 && (
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="font-serif text-2xl text-[#1a1a1a] mb-6 text-center">Your Key Support Areas</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {result.top_conditions.map((cond, i) => (
                <div key={cond.id || i} className="result-section flex items-center gap-4" data-testid={`condition-card-${i}`}>
                  <div className="w-10 h-10 rounded-full bg-[#d4e4bc] flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-serif font-semibold text-[#1a1a1a]">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#1a1a1a]">{cond.consumer_label || cond.name}</h3>
                    {cond.score && <p className="text-xs text-gray-500">Score: {cond.score}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Shopify loading indicator */}
      {loadingShopify && (
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading product details from store...
          </div>
        </div>
      )}

      {/* Protocol Sections */}
      {protocols.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-3">
                {protocols.length > 1 ? 'Recommended Protocols' : 'Recommended Protocol'}
              </p>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1a1a1a]">
                {protocols.length > 1 
                  ? `We identified ${protocols.length} support areas for you` 
                  : protocols[0].consumer_title || protocols[0].protocol_name}
              </h2>
            </div>

            <div className="space-y-8">
              {protocols.map((protocol, pIndex) => {
                const isExpanded = expandedProtocols[protocol.id] !== false || pIndex === 0;
                const products = protocol.products || [];
                const primaryProducts = products.filter(p => p.role === 'primary');
                const secondaryProducts = products.filter(p => p.role === 'secondary');
                const optionalProducts = products.filter(p => p.role === 'optional');

                return (
                  <div key={protocol.id} className="border border-[#d0e4d4] rounded-xl overflow-hidden bg-white" data-testid={`protocol-section-${pIndex}`}>
                    {/* Protocol Header */}
                    <div 
                      className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleProtocol(protocol.id)}
                      data-testid={`protocol-toggle-${pIndex}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#d4e4bc] flex items-center justify-center flex-shrink-0">
                          <span className="font-serif font-semibold text-[#1a1a1a]">{pIndex + 1}</span>
                        </div>
                        <div>
                          <h3 className="font-serif text-xl text-[#1a1a1a]">
                            {protocol.consumer_title || protocol.protocol_name}
                          </h3>
                          {protocol.description && (
                            <p className="text-sm text-gray-500 mt-1 max-w-lg">{protocol.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Button
                          onClick={(e) => { e.stopPropagation(); addProtocolToCart(protocol); }}
                          className="btn-primary hidden sm:flex"
                          data-testid={`add-protocol-${pIndex}`}
                        >
                          <ShoppingCart className="mr-2 w-4 h-4" strokeWidth={1.5} />
                          Add Full Protocol
                        </Button>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>

                    {/* Mobile Add Protocol Button */}
                    <div className="px-6 pb-4 sm:hidden">
                      <Button
                        onClick={() => addProtocolToCart(protocol)}
                        className="btn-primary w-full"
                        data-testid={`add-protocol-mobile-${pIndex}`}
                      >
                        <ShoppingCart className="mr-2 w-4 h-4" strokeWidth={1.5} />
                        Add Full Protocol to Cart
                      </Button>
                    </div>

                    {/* Expanded Products */}
                    {isExpanded && products.length > 0 && (
                      <div className="border-t border-[#d0e4d4] p-6">
                        {/* Product Groups */}
                        {[
                          { label: 'Core Support', items: primaryProducts },
                          { label: 'Targeted Support', items: secondaryProducts },
                          { label: 'Optional', items: optionalProducts },
                        ].filter(g => g.items.length > 0).map(group => (
                          <div key={group.label} className="mb-6 last:mb-0">
                            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-3">{group.label}</p>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {group.items.map((product) => {
                                const pd = getProductData(product);
                                return (
                                  <div key={product.id} className="product-card flex flex-col" data-testid={`product-card-${product.id}`}>
                                    <div className="aspect-square bg-[#F1F6F9] relative overflow-hidden">
                                      {pd.image_url ? (
                                        <img src={pd.image_url} alt={pd.title || pd.product_name} className="w-full h-full object-cover" loading="lazy" />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                                          <ShoppingCart className="w-12 h-12" />
                                        </div>
                                      )}
                                      <div className="absolute top-3 left-3">{getRoleBadge(product.role)}</div>
                                    </div>
                                    <div className="p-4 flex flex-col flex-1">
                                      <h4 className="font-serif text-base text-[#1a1a1a] mb-1">{pd.title || pd.product_name}</h4>
                                      {pd.vendor && <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">{pd.vendor}</p>}
                                      <div className="mt-auto flex items-center justify-between pt-3">
                                        {formatPrice(pd.price) && <span className="text-base font-medium text-[#1a1a1a]">{formatPrice(pd.price)}</span>}
                                        <Button
                                          size="sm"
                                          onClick={() => addToCart(product)}
                                          disabled={isInCart(product.id)}
                                          className={isInCart(product.id) ? 'bg-[#d4e4bc] text-[#1a1a1a] hover:bg-[#c4d4ac] text-xs' : 'btn-primary text-xs'}
                                          data-testid={`add-to-cart-${product.id}`}
                                        >
                                          {isInCart(product.id) ? <><Check className="mr-1 w-3 h-3" /> Added</> : <><Plus className="mr-1 w-3 h-3" /> Add</>}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Checkout Bar */}
            {cart.length > 0 && (
              <div className="mt-8 flex justify-center">
                <Button onClick={proceedToCheckout} disabled={checkingOut} className="btn-primary text-base px-10 py-4" data-testid="proceed-checkout-btn">
                  {checkingOut ? <Loader2 className="mr-2 w-5 h-5 animate-spin" /> : <ExternalLink className="mr-2 w-5 h-5" strokeWidth={1.5} />}
                  Proceed to Checkout ({cart.length} items)
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Recommended For You (Cross-Sell) */}
      {result.cross_sell_products?.length > 0 && (
        <section className="py-12 bg-[#F7F5F0]" data-testid="cross-sell-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-3">
                Complementary Support
              </p>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1a1a1a] mb-3">
                Recommended For You
              </h2>
              <p className="text-gray-600 text-sm max-w-xl mx-auto">
                Based on your support areas, these products may complement your protocol
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {result.cross_sell_products.map((product) => {
                const pd = getProductData(product);
                return (
                  <div key={product.id} className="product-card flex flex-col" data-testid={`cross-sell-${product.id}`}>
                    <div className="aspect-square bg-white relative overflow-hidden">
                      {pd.image_url ? (
                        <img src={pd.image_url} alt={pd.title || pd.product_name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-200">
                          <Package className="w-10 h-10" />
                        </div>
                      )}
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h4 className="font-serif text-sm text-[#1a1a1a] mb-1 line-clamp-2">{pd.title || pd.product_name}</h4>
                      {product.cross_sell_reason && (
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2">{product.cross_sell_reason}</p>
                      )}
                      <div className="mt-auto flex items-center justify-between pt-2">
                        {formatPrice(pd.price) && <span className="text-sm font-medium text-[#1a1a1a]">{formatPrice(pd.price)}</span>}
                        <Button
                          size="sm"
                          onClick={() => addToCart(product)}
                          disabled={isInCart(product.id)}
                          className={isInCart(product.id) ? 'bg-[#d4e4bc] text-[#1a1a1a] hover:bg-[#c4d4ac] text-xs' : 'btn-primary text-xs'}
                          data-testid={`cross-sell-add-${product.id}`}
                        >
                          {isInCart(product.id) ? <><Check className="mr-1 w-3 h-3" /> Added</> : <><Plus className="mr-1 w-3 h-3" /> Add</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* No Products */}
      {protocols.length === 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-serif text-xl text-[#1a1a1a] mb-4">Protocol Setup in Progress</h3>
            <p className="text-gray-600 mb-6 text-sm">We're still configuring product recommendations. Please check back soon.</p>
            <Button onClick={() => navigate(isEmbed ? '/embed' : '/quiz')} variant="outline" className="btn-outline" data-testid="retake-quiz-btn">Retake Quiz</Button>
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="disclaimer-box">
            <p className="flex items-start gap-3">
              <Info className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <span>{result.disclaimer}</span>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">Questions About Your Results?</h2>
          <p className="text-white/70 mb-8 text-sm">Our team is here to help you understand your personalized protocol.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate(isEmbed ? '/embed' : '/quiz')} className="bg-[#d4e4bc] text-[#1a1a1a] hover:bg-[#c4d4ac] border-0 px-8 py-3" style={{ borderRadius: '4px' }} data-testid="retake-quiz-results-btn">
              Retake Quiz
            </Button>
            {!isEmbed && (
              <Button onClick={() => navigate('/')} className="bg-white/10 text-white hover:bg-white/20 border-0 px-8 py-3" style={{ borderRadius: '4px' }} data-testid="back-home-btn">
                Back to Home
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      {!isEmbed && (
        <footer className="py-8 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-sm text-gray-500">These recommendations are for educational purposes only and are not medical advice.</p>
          </div>
        </footer>
      )}
    </div>
  );
};

export default ResultsPage;
